import React, { useState } from 'react';
import cn from "classnames";

import './DeviceCounts.scss';
import { ICounts } from 'interfaces';
import { Progress, ProgressBar, Tooltip } from 'components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { pluralize } from 'services/Utils';
import { UpdateState } from 'enums';

export interface IDeviceCountsProps {
    // If the stage is currently running, we want to display inProgress devices as animated.
    animateInProgress: boolean,
    className?: string,
    counts: ICounts,
    hasStarted: boolean,
}

export function DeviceCounts({ animateInProgress, className, counts, hasStarted }: IDeviceCountsProps) {
    const [popReferenceEl, setPopReferenceEl] = useState(null);
    const [popShowOnEl, setPopShowOnEl] = useState(null);

    if (!counts) {
        counts = {total: 0, totalLocations: 0, success: 0, inProgress: 0, error: 0, isValid: false};
    }

    let successPercent = 0;
    let errorPercent = 0;
    let inProgressPercent = 0;
    // We need to make sure "total" isn't 0, or javascript will return NaN when we divide the counts.
    if (counts.total > 0) {
        successPercent = (counts.success / counts.total) * 100;
        errorPercent = (counts.error / counts.total) * 100;
        inProgressPercent = (counts.inProgress / counts.total) * 100;
    }

    const successText = hasStarted
        ? 'Success'
        : 'Already on target version';

    return <div className={cn('deviceCounts', className)} ref={setPopShowOnEl} tabIndex={0}>
        <div className="deviceCounts_total">
            <span className="deviceCounts_totalItem">
                <FontAwesomeIcon icon="hdd" className="deviceCounts_totalIcon" />
                {counts.total} {pluralize(counts.total, 'device', 'devices')}
            </span>
            <span className="deviceCounts_totalItem">
                <FontAwesomeIcon icon="home" className="deviceCounts_totalIcon" />
                {counts.totalLocations} {pluralize(counts.totalLocations, 'location', 'locations')}
            </span>
        </div>
        <div className="deviceCounts_barWrap" ref={setPopReferenceEl}>
            <Progress>
                <ProgressBar value={successPercent} variant="success">
                    {counts.success > 0 && counts.success}
                </ProgressBar>
                <ProgressBar value={errorPercent} variant="danger">
                    {counts.error > 0 && counts.error}
                </ProgressBar>
                {!animateInProgress && (
                    <ProgressBar value={inProgressPercent} variant="warn">
                        {counts.inProgress > 0 && counts.inProgress}
                    </ProgressBar>
                )}
                {animateInProgress && (
                    <ProgressBar 
                        value={inProgressPercent}
                        variant="info"
                        animated={true}
                        striped={true}
                        className="deviceCounts_runningBar"
                    >
                        {counts.inProgress > 0 && counts.inProgress}
                    </ProgressBar>
                )}
            </Progress>
        </div>

        <Tooltip
            placement="top"
            referenceElement={popReferenceEl}
            showOnElement={popShowOnEl}
            showOnElementDelay={500}
            showOnElementEvents={['hover', 'focus']} 
        >
            <table className="deviceCounts_tooltipTable"><tbody> 
                <tr className="deviceCounts_tooltipItem">
                    <td><i className="deviceCounts_tooltipDot success" /> {counts.success}</td>
                    <td className="deviceCounts_tooltipItemLabel">{successText}</td>
                </tr>
                <tr className="deviceCounts_tooltipItem">
                    <td>
                        {animateInProgress && (<>
                            <i className="deviceCounts_tooltipDot running" /> {counts.inProgress}
                        </>)}
                        {!animateInProgress && (<>
                            <i className="deviceCounts_tooltipDot warn" /> {counts.inProgress}
                        </>)}
                    </td>
                    <td className="deviceCounts_tooltipItemLabel">Awaiting response</td>
                </tr>
                <tr className="deviceCounts_tooltipItem">
                    <td><i className="deviceCounts_tooltipDot error" /> {counts.error}</td>
                    <td className="deviceCounts_tooltipItemLabel">Error</td>
                </tr>
            </tbody></table>
        </Tooltip>
    </div>
};